"use client";

import { useCallback, useEffect, useRef, useState } from "react";

const THREAD_KEY = "twin-thread-id";
const MESSAGES_KEY = "twin-messages";
const MAX_STORED_MESSAGES = 30;
const LEAVE_MESSAGE_TOOL = "leave_message";

function createThreadId() {
  return `t-${crypto.randomUUID()}`;
}

function readStoredMessages() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(MESSAGES_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// 把 SSE 文本流切成 { event, data }；一次 read 可能包含多帧或半帧
function createSseParser(onEvent) {
  let buffer = "";

  return (text) => {
    buffer += text;
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";

    for (const frame of frames) {
      let event = "message";
      let data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event: ")) event = line.slice(7);
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      try {
        onEvent(event, data ? JSON.parse(data) : {});
      } catch {
        // 丢弃无法解析的帧
      }
    }
  };
}

async function readErrorCode(response) {
  try {
    const body = await response.json();
    return { code: body.code ?? "model_failed", retryAfterSec: body.retryAfterSec };
  } catch {
    return { code: "model_failed" };
  }
}

function toResumePayload(decision) {
  if (decision.type === "edit") {
    return {
      decisions: [
        { type: "edit", editedAction: { name: LEAVE_MESSAGE_TOOL, args: { message: decision.message } } },
      ],
    };
  }
  return { decisions: [{ type: decision.type }] };
}

/**
 * 分身对话状态：消息列表、流式发送、停止、重试、新对话、留言确认。
 * status: idle | waiting（已发出、首字未到）| streaming | error
 * 助手消息上的 approval.status: pending → sending → sent / failed；或 cancelled / expired
 */
export function useTwinChat() {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const threadIdRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    threadIdRef.current = window.localStorage.getItem(THREAD_KEY) || createThreadId();
    window.localStorage.setItem(THREAD_KEY, threadIdRef.current);
    setMessages(readStoredMessages());
  }, []);

  useEffect(() => {
    if (status === "waiting" || status === "streaming") return;
    window.localStorage.setItem(
      MESSAGES_KEY,
      JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)),
    );
  }, [messages, status]);

  const updateMessage = useCallback((id, update) => {
    setMessages((current) =>
      current.map((message) => (message.id === id ? { ...message, ...update(message) } : message)),
    );
  }, []);

  const hasPendingApproval = messages.some((message) =>
    ["pending", "sending"].includes(message.approval?.status),
  );

  /**
   * 发一次请求并把 SSE 写进 assistantId 对应的气泡，返回失败信息（null 表示成功）。
   * approvalMessageId：本轮是在回传哪张确认卡的结果，用于接收 message_result。
   */
  const runTurn = useCallback(
    async (payload, assistantId, { approvalMessageId } = {}) => {
      const controller = new AbortController();
      abortRef.current = controller;

      let failure = null;
      let receivedText = false;
      let awaitingApproval = false;

      try {
        const response = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ threadId: threadIdRef.current, ...payload }),
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          failure = await readErrorCode(response);
        } else {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          const parse = createSseParser((event, data) => {
            if (event === "token") {
              receivedText = true;
              setStatus("streaming");
              updateMessage(assistantId, (message) => ({ content: message.content + data.text }));
            } else if (event === "tool") {
              updateMessage(assistantId, () => ({ activity: data.name }));
            } else if (event === "sources") {
              updateMessage(assistantId, () => ({ sources: data.sources }));
            } else if (event === "approval") {
              awaitingApproval = true;
              updateMessage(assistantId, () => ({ approval: { draft: data.draft, status: "pending" } }));
            } else if (event === "message_result" && approvalMessageId) {
              updateMessage(approvalMessageId, (message) =>
                message.approval.status === "cancelled"
                  ? {}
                  : { approval: { ...message.approval, status: data.ok ? "sent" : "failed" } },
              );
            } else if (event === "done" && data.awaitingApproval) {
              awaitingApproval = true;
            } else if (event === "error") {
              failure = { code: data.code ?? "model_failed" };
            }
          });

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            parse(decoder.decode(value, { stream: true }));
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") failure = { code: "network" };
      } finally {
        abortRef.current = null;
      }

      if (!failure && !receivedText && !awaitingApproval && !controller.signal.aborted) {
        failure = { code: "empty_answer" };
      }

      // 没内容也没确认卡的助手气泡不保留
      setMessages((current) =>
        current.filter((message) => message.id !== assistantId || message.content || message.approval),
      );
      return failure;
    },
    [updateMessage],
  );

  const send = useCallback(
    async (rawText) => {
      const text = rawText.trim();
      if (!text || abortRef.current || hasPendingApproval) return;

      const assistantId = crypto.randomUUID();
      setError(null);
      setStatus("waiting");
      setMessages((current) => [
        ...current,
        { id: crypto.randomUUID(), role: "user", content: text },
        { id: assistantId, role: "assistant", content: "" },
      ]);

      const failure = await runTurn({ message: text }, assistantId);
      if (failure) {
        setError({ ...failure, retryText: text });
        setStatus("error");
      } else {
        setStatus("idle");
      }
    },
    [runTurn, hasPendingApproval],
  );

  /**
   * 回传确认卡结果。decision: { type: "approve" } | { type: "edit", message } | { type: "reject" }
   * 后续回答流进一个新的助手气泡；请求没到达服务端时卡片退回 pending，访客可以再点一次。
   */
  const resolveApproval = useCallback(
    async (messageId, decision) => {
      if (abortRef.current) return;

      const isReject = decision.type === "reject";
      updateMessage(messageId, (message) => ({
        approval: {
          ...message.approval,
          status: isReject ? "cancelled" : "sending",
          finalText: decision.type === "edit" ? decision.message : message.approval.draft,
        },
      }));

      const assistantId = crypto.randomUUID();
      setError(null);
      setStatus("waiting");
      setMessages((current) => [...current, { id: assistantId, role: "assistant", content: "" }]);

      const failure = await runTurn({ resume: toResumePayload(decision) }, assistantId, {
        approvalMessageId: messageId,
      });

      if (failure) {
        const expired = failure.code === "nothing_to_resume";
        updateMessage(messageId, (message) => ({
          approval: { ...message.approval, status: expired ? "expired" : "pending" },
        }));
        setError(failure);
        setStatus("error");
      } else {
        setStatus("idle");
      }
    },
    [runTurn, updateMessage],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const retry = useCallback(() => {
    if (!error?.retryText) return;
    const retryText = error.retryText;
    // 去掉上一次失败的那条用户消息，避免重复
    setMessages((current) => {
      const lastIndex = current.findLastIndex(
        (message) => message.role === "user" && message.content === retryText,
      );
      return lastIndex === -1 ? current : current.filter((_, index) => index !== lastIndex);
    });
    send(retryText);
  }, [error, send]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    threadIdRef.current = createThreadId();
    window.localStorage.setItem(THREAD_KEY, threadIdRef.current);
    setMessages([]);
    setError(null);
    setStatus("idle");
  }, []);

  return {
    messages,
    status,
    error,
    hasPendingApproval,
    send,
    stop,
    retry,
    reset,
    resolveApproval,
  };
}
